import type { ReactElement } from 'react';

import type { ChatErrorCode } from '@harness/protocol';
import type { StreamErrorState } from '@harness/stream-core';

/**
 * One component, ten distinct treatments keyed by code. The copy is the
 * specification's, verbatim: what happened, then what to do. Retryable codes
 * get a retry control; content_blocked and context_too_large do not, because
 * retrying cannot change the answer.
 */

type Affordance = 'auto' | 'retry' | 'resume' | 'restart' | 'none';

interface Treatment {
  copy: (error: StreamErrorState, categories: readonly string[]) => string;
  affordance: Affordance;
}

const TREATMENTS: Record<ChatErrorCode, Treatment> = {
  rate_limited: {
    copy: (error) =>
      error.retryAfterSeconds === undefined
        ? 'Too many requests.'
        : `Too many requests. Retrying in ${String(error.retryAfterSeconds)} seconds.`,
    affordance: 'auto',
  },
  context_too_large: {
    copy: () =>
      'This conversation is too long to continue. Start a new one, or remove earlier messages.',
    affordance: 'none',
  },
  content_blocked: {
    copy: (_error, categories) =>
      categories.length > 0
        ? `The response was withheld by a content check. Categories: ${categories.join(', ')}.`
        : 'The response was withheld by a content check.',
    affordance: 'none',
  },
  upstream_unavailable: {
    copy: () => 'The model is not reachable right now. Retry, or try again shortly.',
    affordance: 'retry',
  },
  deadline_exceeded: {
    copy: () => 'The response took too long and was cut short. The partial text is kept below.',
    affordance: 'retry',
  },
  stalled: {
    copy: () => 'No output for 20 seconds. The connection is open but nothing is arriving.',
    affordance: 'retry',
  },
  stream_aborted: {
    copy: () => 'Stopped. The partial response is kept below.',
    affordance: 'resume',
  },
  stream_expired: {
    copy: () =>
      'The stream can no longer be resumed. Too much time passed, so the buffered output was released.',
    affordance: 'restart',
  },
  internal_error: {
    copy: () => 'The server failed while generating. Retry.',
    affordance: 'retry',
  },
  unknown: {
    copy: (error) =>
      `The stream ended with an unrecognised error: ${error.message ?? 'no detail given'}`,
    affordance: 'retry',
  },
};

export function StreamError({
  error,
  categories,
  live,
  onRetry,
  onRestart,
}: {
  error: StreamErrorState;
  /** Categories from a redaction, for the content_blocked copy. */
  categories: readonly string[];
  /** Controls act only on the newest message, whose machine still exists. */
  live: boolean;
  onRetry: () => void;
  onRestart: () => void;
}): ReactElement {
  // TREATMENTS is keyed by the full ChatErrorCode union, so this index is
  // exhaustive and total: every code the reducer can produce has an entry.
  const treatment = TREATMENTS[error.code];

  return (
    <div className="notice notice--fault" role="alert">
      <div className="stream-error__head">
        <span className="notice__title mono">{error.code}</span>
        {treatment.affordance === 'auto' && <span className="mono muted">retrying</span>}
      </div>
      <p>{treatment.copy(error, categories)}</p>
      {live && treatment.affordance === 'retry' && (
        <div>
          <button type="button" className="button" onClick={onRetry}>
            Retry
          </button>
        </div>
      )}
      {live && treatment.affordance === 'resume' && (
        <div>
          <button type="button" className="button" onClick={onRetry}>
            Resume
          </button>
        </div>
      )}
      {live && treatment.affordance === 'restart' && (
        <div>
          <button type="button" className="button" onClick={onRestart}>
            Start a new stream
          </button>
        </div>
      )}
    </div>
  );
}

/** The stalled phase notice. Not an error state: the connection is healthy. */
export function StalledNotice({
  live,
  onRetry,
}: {
  live: boolean;
  onRetry: () => void;
}): ReactElement {
  return (
    <div className="notice notice--hold" role="status" aria-live="polite">
      <span className="notice__title mono">stalled</span>
      <p>No output for 20 seconds. The connection is open but nothing is arriving.</p>
      {live && (
        <div>
          <button type="button" className="button" onClick={onRetry}>
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
